import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function AProposPage() {
  // SEO Configuration
  useSEO({
    title: 'À Propos d\'Izislide - Designer PowerPoint | Notre Mission & Équipe',
    description: 'Imagez vos idées avec Izislide. Designer PowerPoint expert en création de présentations PPT professionnelles. Découvrez notre mission, notre équipe et nos valeurs.',
    keywords: 'à propos izislide, designer powerpoint, équipe présentation, mission izislide, agence powerpoint paris',
    canonical: '/a-propos',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      mainEntity: {
        '@type': 'Organization',
        name: 'Izislide',
        slogan: 'Imagez vos idées',
        description: 'Designer PowerPoint expert en création de présentations PowerPoint professionnelles',
        foundingDate: '2020',
        numberOfEmployees: '10-20',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '123 Avenue des Champs-Élysées',
          addressLocality: 'Paris',
          postalCode: '75008',
          addressCountry: 'FR'
        }
      }
    }
  });

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section 
        className="relative py-32 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 50%, rgba(0,168,107,0.1) 100%), url('https://public.readdy.ai/ai/img_res/dbf3073ef69e28a45aa60d626937d51f.jpg')`
        }}
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Imagez vos idées : Nous transformons vos présentations PowerPoint en outils mémorables
            </h1>
            <p className="text-xl text-gray-600 mb-10">
              Une équipe de designers PowerPoint experts passionnés par le design et le storytelling de présentation
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Notre mission
            </h2>
            <div className="text-lg text-gray-700 space-y-6">
              <p>
                Chez Izislide, nous croyons que chaque <strong>présentation PowerPoint</strong> est une opportunité de marquer les esprits, 
                de convaincre et d'inspirer. Pourtant, trop souvent, des idées brillantes se perdent dans des 
                <strong> slides PowerPoint</strong> confuses et peu engageantes.
              </p>
              <p className="text-2xl font-semibold text-[#00A86B] text-center py-8">
                "<strong>Imagez vos idées</strong> : Nous transformons vos présentations PowerPoint en outils puissants de persuasion, clairs, impactants et professionnels."
              </p>
              <p>
                Que vous soyez une agence de communication événementielle, une entreprise, un formateur ou un professionnel 
                indépendant, nos <strong>designers PowerPoint</strong> mettent leur expertise au service de votre réussite.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Notre expertise de designer PowerPoint
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-[#F7A348]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-palette-line text-4xl text-[#F7A348]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Design PowerPoint visuel</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#00A86B] text-xl mr-3 mt-1"></i>
                    <span>Création de <strong>présentations PowerPoint</strong> sur mesure</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#00A86B] text-xl mr-3 mt-1"></i>
                    <span>Templates PowerPoint personnalisés</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#00A86B] text-xl mr-3 mt-1"></i>
                    <span>Infographies et datavisualisations dans PowerPoint</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#00A86B] text-xl mr-3 mt-1"></i>
                    <span>Motion design et animations PowerPoint</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-[#00A86B]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-quill-pen-line text-4xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Stratégie éditoriale pour présentations PPT</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#00A86B] text-xl mr-3 mt-1"></i>
                    <span>Réécriture et clarification de vos <strong>présentations PowerPoint</strong></span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#00A86B] text-xl mr-3 mt-1"></i>
                    <span>Structuration de messages dans vos slides PowerPoint</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#00A86B] text-xl mr-3 mt-1"></i>
                    <span>Storytelling impactant pour <strong>présentations PPT</strong></span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-[#00A86B] text-xl mr-3 mt-1"></i>
                    <span>Adaptation de vos présentations PowerPoint à l'audience</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Équipe */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Notre équipe de designers PowerPoint
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-32 h-32 bg-gradient-to-br from-[#F7A348] to-[#e89537] rounded-full mx-auto mb-6 flex items-center justify-center text-white text-4xl font-bold">
                  CM
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Claire Martin</h3>
                <p className="text-[#00A86B] font-semibold mb-3">Fondatrice & Designer PowerPoint Senior</p>
                <p className="text-gray-600">
                  15 ans d'expérience en design PowerPoint et communication. Passionnée par l'art de rendre les idées visuelles. <strong>Imagez vos idées</strong> est sa devise.
                </p>
              </div>

              <div className="text-center">
                <div className="w-32 h-32 bg-gradient-to-br from-[#00A86B] to-[#008f5c] rounded-full mx-auto mb-6 flex items-center justify-center text-white text-4xl font-bold">
                  JD
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Julien Dubois</h3>
                <p className="text-[#00A86B] font-semibold mb-3">Expert Storytelling PowerPoint</p>
                <p className="text-gray-600">
                  Ancien consultant en stratégie, spécialiste de la structuration de messages impactants dans les <strong>présentations PowerPoint</strong>.
                </p>
              </div>

              <div className="text-center">
                <div className="w-32 h-32 bg-gradient-to-br from-[#C9D86A] to-[#b8c55f] rounded-full mx-auto mb-6 flex items-center justify-center text-gray-900 text-4xl font-bold">
                  SL
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Sophie Laurent</h3>
                <p className="text-[#00A86B] font-semibold mb-3">Designer PowerPoint Senior</p>
                <p className="text-gray-600">
                  Graphiste de formation, experte en identité visuelle et création de templates PowerPoint personnalisés.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Nos valeurs
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl text-center">
                <div className="w-16 h-16 bg-[#F7A348]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-star-line text-3xl text-[#F7A348]"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Excellence</h3>
                <p className="text-gray-600">
                  Nous visons la perfection dans chaque détail, chaque slide PowerPoint, chaque projet de présentation PPT.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl text-center">
                <div className="w-16 h-16 bg-[#00A86B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-time-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Réactivité</h3>
                <p className="text-gray-600">
                  Nous comprenons vos urgences de <strong>présentations PowerPoint</strong> et nous adaptons à vos contraintes de temps.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl text-center">
                <div className="w-16 h-16 bg-[#C9D86A]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-heart-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Passion</h3>
                <p className="text-gray-600">
                  Nous aimons le design PowerPoint et ça se voit dans chaque création de présentation PPT.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chiffres clés */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Izislide en chiffres
            </h2>
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-[#00A86B] mb-3">500+</div>
                <p className="text-xl text-gray-700">Clients satisfaits</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-[#F7A348] mb-3">5000+</div>
                <p className="text-xl text-gray-700">Présentations PowerPoint créées</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-[#00A86B] mb-3">98%</div>
                <p className="text-xl text-gray-700">Taux de satisfaction</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-[#F7A348] mb-3">48h</div>
                <p className="text-xl text-gray-700">Délai moyen</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#00A86B] to-[#008f5c]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Travaillons ensemble sur vos présentations PowerPoint
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Discutons de votre projet PPT et découvrez comment notre designer PowerPoint peut vous aider
            </p>
            <Link 
              to="/contact" 
              className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}